import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiKey, MAPY_API_CONFIG, PanoramaInstance, PanoramaExistsResult } from '../config/mapyApi';
import { emitApiLimitExceeded } from '../context/ApiLimitContext';
import {
    throttledRequest,
    isRateLimitError,
    setRateLimited,
    resetErrorCount,
    getBackoffDelay,
    sleep,
} from '../util/apiThrottle';

// Globální reference na Panorama objekt
declare global {
    interface Window {
        Panorama?: {
            panoramaFromPosition: (options: PanoramaFromPositionOptions) => Promise<PanoramaInstance>;
            panoramaExists: (options: PanoramaExistsOptions) => Promise<PanoramaExistsResult>;
        };
    }
}

interface PanoramaFromPositionOptions {
    parent: HTMLElement;
    lon: number;
    lat: number;
    apiKey: string;
    radius?: number;
    yaw?: 'auto' | 'point' | number;
    pitch?: number;
    fov?: number;
    showNavigation?: boolean;
    lang?: string;
    hideErrors?: boolean;
}

interface PanoramaExistsOptions {
    lon: number;
    lat: number;
    apiKey: string;
    radius?: number;
}

interface UsePanoramaLoaderReturn {
    /** Je Panorama API načteno */
    loaded: boolean;
    /** Chyba při načítání */
    error: Error | null;
    /** Vytvoří panorama na dané pozici */
    createPanorama: (
        element: HTMLElement,
        lon: number,
        lat: number,
        options?: Partial<Omit<PanoramaFromPositionOptions, 'parent' | 'lon' | 'lat' | 'apiKey'>>
    ) => Promise<PanoramaInstance | null>;
    /** Ověří existenci panoramatu */
    checkPanoramaExists: (
        lon: number,
        lat: number,
        radius?: number
    ) => Promise<PanoramaExistsResult | null>;
}

// Singleton pro sledování stavu načítání scriptu
let scriptLoadingPromise: Promise<void> | null = null;
let isScriptLoaded = false;

/**
 * Hook pro práci s novým JS Panorama API
 *
 * Nahrazuje staré SMap.Pano.Scene a SMap.Pano.getBest
 *
 * @example
 * const { loaded, createPanorama, checkPanoramaExists } = usePanoramaLoader();
 *
 * useEffect(() => {
 *     if (loaded && containerRef.current) {
 *         createPanorama(containerRef.current, 16.6, 49.2, { showNavigation: true });
 *     }
 * }, [loaded]);
 */
export const usePanoramaLoader = (): UsePanoramaLoaderReturn => {
    const [loaded, setLoaded] = useState(isScriptLoaded);
    const [error, setError] = useState<Error | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;

        const loadScript = async () => {
            // Pokud už je načteno, přeskočíme
            if (isScriptLoaded && window.Panorama) {
                setLoaded(true);
                return;
            }

            // Pokud se už načítá, počkáme
            if (scriptLoadingPromise) {
                try {
                    await scriptLoadingPromise;
                    if (mountedRef.current) {
                        setLoaded(true);
                    }
                } catch (err) {
                    if (mountedRef.current) {
                        setError(err as Error);
                    }
                }
                return;
            }

            // Začneme načítání
            scriptLoadingPromise = new Promise<void>((resolve, reject) => {
                const script = document.createElement('script');
                script.src = MAPY_API_CONFIG.panorama.scriptUrl;
                script.async = true;

                script.onload = () => {
                    isScriptLoaded = true;
                    resolve();
                };

                script.onerror = () => {
                    const err = new Error('Failed to load Panorama script');
                    scriptLoadingPromise = null;
                    reject(err);
                };

                document.head.appendChild(script);
            });

            try {
                await scriptLoadingPromise;
                if (mountedRef.current) {
                    setLoaded(true);
                }
            } catch (err) {
                if (mountedRef.current) {
                    setError(err as Error);
                }
            }
        };

        loadScript();

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const createPanorama = useCallback(async (
        element: HTMLElement,
        lon: number,
        lat: number,
        options: Partial<Omit<PanoramaFromPositionOptions, 'parent' | 'lon' | 'lat' | 'apiKey'>> = {}
    ): Promise<PanoramaInstance | null> => {
        if (!window.Panorama) {
            console.error('Panorama API not loaded');
            return null;
        }

        try {
            const result = await throttledRequest(async () => {
                return window.Panorama!.panoramaFromPosition({
                    parent: element,
                    lon,
                    lat,
                    apiKey: getApiKey(),
                    radius: options.radius ?? MAPY_API_CONFIG.panorama.defaultRadius,
                    showNavigation: options.showNavigation ?? true,
                    lang: options.lang ?? 'cs',
                    hideErrors: options.hideErrors ?? true,
                    yaw: options.yaw,
                    pitch: options.pitch,
                    fov: options.fov,
                });
            });

            // Kontrola na chybu limitu
            if (result.errorCode === 'WRONG_API_KEY' || result.errorCode === 'MISSING_API_KEY') {
                emitApiLimitExceeded();
            } else if (result.errorCode === 'LIMIT_EXCEEDED' || isRateLimitError(result)) {
                setRateLimited(getBackoffDelay());
                console.warn('Panorama API rate limit - backing off');
            } else if (result.errorCode === 'NONE') {
                // Úspěšný request - reset error counteru
                resetErrorCount();
            }

            return result;
        } catch (err) {
            console.error('Error creating panorama:', err);

            // Kontrola na rate limit error
            if (isRateLimitError(err)) {
                setRateLimited(getBackoffDelay());
                console.warn('Panorama API rate limit (exception) - backing off');
            }
            // Kontrola na 403 (API key limit)
            else if (err instanceof Error && (err.message.includes('403') || err.message.includes('limit'))) {
                emitApiLimitExceeded();
            }

            return null;
        }
    }, []);

    const checkPanoramaExists = useCallback(async (
        lon: number,
        lat: number,
        radius?: number
    ): Promise<PanoramaExistsResult | null> => {
        if (!window.Panorama) {
            console.error('Panorama API not loaded');
            return null;
        }

        try {
            const result = await throttledRequest(async () => {
                return window.Panorama!.panoramaExists({
                    lon,
                    lat,
                    apiKey: getApiKey(),
                    radius: radius ?? MAPY_API_CONFIG.panorama.defaultRadius,
                });
            });

            // Kontrola na chybu limitu
            if (result.errorCode === 'WRONG_API_KEY' || result.errorCode === 'MISSING_API_KEY') {
                emitApiLimitExceeded();
            } else if (result.errorCode === 'LIMIT_EXCEEDED' || isRateLimitError(result)) {
                setRateLimited(getBackoffDelay());
                console.warn('Panorama exists API rate limit - backing off');
            } else if (result.exists) {
                // Úspěšný request - reset error counteru
                resetErrorCount();
            }

            return result;
        } catch (err) {
            console.error('Error checking panorama existence:', err);

            // Kontrola na rate limit error
            if (isRateLimitError(err)) {
                setRateLimited(getBackoffDelay());
                console.warn('Panorama exists API rate limit (exception) - backing off');
            }
            // Kontrola na 403 (API key limit)
            else if (err instanceof Error && (err.message.includes('403') || err.message.includes('limit'))) {
                emitApiLimitExceeded();
            }

            return null;
        }
    }, []);

    return {
        loaded,
        error,
        createPanorama,
        checkPanoramaExists,
    };
};

export default usePanoramaLoader;
